import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { t } from 'i18next'
import { useRecoilValue } from 'recoil'
import { Button, Card, CardBody, CardFooter } from 'reactstrap'
import Swal from 'sweetalert2'
import moment from 'moment'

import env from '../../env/src_config'
import { headersState } from '../../recoil/recoil'
import { axiosError } from '../../helpers/response'
import { isNull, dateFormatForBE } from '../../izUtils'
import { transformer, linker } from '../../helpers/fields'
import Spinner from '../spinner/Spinner'
import { errorStatus } from '../../helpers/response'
import RelatedTasks from './RelatedTasks'
import TechStandby from './TechStandby'

const AddUpdateException = ({ selectedUsers, setSelectedUsers }) => {
    const Navigate = useNavigate();
    const { exceptionId } = useParams()
    const [searchParams] = useSearchParams();

    const headers = useRecoilValue(headersState);
    const [inputs, setInputs] = useState(null)
    const [relatedTasks, setRelatedTasks] = useState(null)
    const [futureExceptions, setFutureExceptions] = useState(null)
    const [techStandby, setTechStandby] = useState([])
    const [showErrors, setShowErrors] = useState(false)
    const [techSubstitutes, setTechSubstitutes] = useState({})

    const firstUpdate = useRef(true);
    const skipSelectedUsersUpdate = useRef(false);

    const latestInputsRef = useRef(null);
    const updateDataInFlightRef = useRef(false);
    const pendingSyncRef = useRef(null);
    const idleWaitersRef = useRef([]);

    useEffect(() => {
        latestInputsRef.current = inputs;
    }, [inputs])

    const queueSync = (clonedInputs) => {
        latestInputsRef.current = clonedInputs;
        if (updateDataInFlightRef.current) {
            pendingSyncRef.current = clonedInputs;
            return;
        }
        updateData(false, clonedInputs);
    }

    const waitForIdleSync = () => {
        if (!updateDataInFlightRef.current) return Promise.resolve();
        return new Promise((resolve) => idleWaitersRef.current.push(resolve));
    }

    useEffect(() => {
        if (!isNull(inputs)) setInputs(null)
        setShowErrors(false);
        skipSelectedUsersUpdate.current = true;

        let date = searchParams.get("date");
        let payload = null;

        if (exceptionId === 'create' && !isNull(date)) {
            const parsedDate = moment(date, 'YYYY-MM-DD', true);

            if (parsedDate.isValid()) {
                const dateFrom = dateFormatForBE(parsedDate.clone().hour(7).minute(30).second(0).utc().format(), true);
                const dateTo = dateFormatForBE(parsedDate.clone().hour(15).minute(30).second(0).utc().format(), true);
                payload = {
                    exception_from: { value: dateFrom },
                    exception_to: { value: dateTo },
                };
            }
        }

        updateData(false, payload);
    }, [exceptionId]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        if (skipSelectedUsersUpdate.current) {
            skipSelectedUsersUpdate.current = false;
            return;
        }

        queueSync(latestInputsRef.current || inputs);
    }, [selectedUsers]) // eslint-disable-line react-hooks/exhaustive-deps

    const updateData = (save, passedInputs) => {
        updateDataInFlightRef.current = true;
        let payload = { save, data: {} };
        if (!isNull(passedInputs)) {
            const keys = Object.keys(passedInputs);
            for (let i = 0; i < keys.length; i++) {
                if (!isNull(passedInputs[keys[i]].value) && passedInputs[keys[i]].value.length !== 0) {
                    payload.data[keys[i]] = passedInputs[keys[i]].value;
                } else {
                    payload.data[keys[i]] = "";
                }
            }
        }

        const techniciansPayload = selectedUsers.map(id => {
            const entry = { id };
            if (techSubstitutes[id]) entry.substitute_technician_id = techSubstitutes[id];
            return entry;
        });

        let method = 'patch';
        if (exceptionId === 'create') {
            payload.data.technicians = techniciansPayload;
            method = "post";
        } else {
            payload.data.technicians = techniciansPayload;
        }

        axios[method](env.api + '/api/user/exception/' + exceptionId, payload, {headers}).then(response => {
            if (save) {
                if (!isNull(response.data.state) && response.data.state === 'success') {
                    let title = t('schedule.exception.update.success.title');
                    if (exceptionId === 'create') title = t('schedule.exception.create.success.title');
                    Swal.fire({
                        title,
                        text: response.data.message,
                        icon: 'success',
                        confirmButtonColor: 'var(--theme-default)',
                        confirmButtonText: t('ok'),
                    })
                    skipSelectedUsersUpdate.current = true;
                    setSelectedUsers([]);
                } else {
                    setShowErrors(true)
                    errorStatus(response.data, t);
                }
            }

            if (!isNull(response.data.data)) {
                let transformedInputs = transformer(response.data.data);
                setInputs(transformedInputs);
                let standbyTechs = [];

                transformedInputs.technicians?.values.forEach(tech => {
                    if (selectedUsers.indexOf(tech.id) !== -1) {
                        if (tech.standby && tech.standby.length > 0) {
                            standbyTechs = standbyTechs.concat(tech);
                        }
                    }
                })

                setTechStandby(standbyTechs);
            }

            setRelatedTasks(response.data.technicians);
            setFutureExceptions(response.data.future_excpetions);
        }).catch(error => {
            axiosError(error, Navigate);
        }).finally(() => {
            updateDataInFlightRef.current = false;
            if (pendingSyncRef.current) {
                const queuedInputs = pendingSyncRef.current;
                pendingSyncRef.current = null;
                updateData(false, queuedInputs);
            } else {
                const waiters = idleWaitersRef.current;
                idleWaitersRef.current = [];
                waiters.forEach(resolve => resolve());
            }
        });
    }

    const textChange = (value, name) => {
        let clonedInputs = { ...(latestInputsRef.current || inputs) }
        clonedInputs[name] = { ...clonedInputs[name], value };
        queueSync(clonedInputs);
    }

    const handleStandbyChange = (event, techId) => {
        const substituteId = event.target.value;
        setTechSubstitutes(prev => ({
            ...prev,
            [techId]: substituteId
        }));
    }

    // Create fields
    const mapLinker = (field) => {
        const inputlinkerFields = {
            field,
            inputs,
            showErrors,
            futureExceptions,
            component: {id: 'addUpdateException'},
            textHandler: (value, id) => textChange(value, id),
            selectHandler: (value, id) => textChange(value, id),
            booleanHandler: (value, id) => textChange(value, id),
            dateHandler: (value, id) => textChange(value, id),
        }

        return linker(inputlinkerFields);
    }

    return (
        <Card id="addUpdateException" className="ribbon-wrapper">
            <CardBody>
                <div className="ribbon ribbon-clip ribbon-primary">{(exceptionId === 'create' ? t('schedule.exception.create.title') : t('schedule.exception.update.title'))}</div>
                {isNull(inputs) ?
                    <Spinner />
                    :
                    <div className="theme-form">
                        { ['technicians', 'exception', 'name', 'exception_from', 'exception_to', 'work_days', 'substitute_technician'].map(field => mapLinker(field)) }

                        {/* Standby Technicians */}
                        {inputs.exception.value === 'sick_days' && <TechStandby techStandby={techStandby} handleStandbyChange={handleStandbyChange} />}

                        {/* Related Tasks */}
                        <RelatedTasks relatedTasks={relatedTasks} />
                    </div>
                }
            </CardBody>
            <CardFooter>
                <div className='text-end'>
                    <Button color="primary" className="mt-2 me-2" onClick={() => waitForIdleSync().then(() => updateData(true, latestInputsRef.current || inputs))}>{(exceptionId === 'create') ? t('add') : t('save')}</Button>
                </div>
            </CardFooter>
        </Card>
    )
}

export default AddUpdateException